import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./editor.css";
import "./emojiButton.css";
import EmojiPicker from "./EmojiPicker";

import { registerSmartBreak } from "./registerSmartBreak";

const Parchment = Quill.import("parchment");

registerSmartBreak();


function lineBreakMatcher() {
  var Delta = Quill.import("delta");
  var newDelta = new Delta();
  newDelta.insert({ break: "" });
  return newDelta;
}

function handleLinebreak(range, context) {
  const { quill } = this;
  const currentLeaf = quill.getLeaf(range.index)[0];
  const nextLeaf = quill.getLeaf(range.index + 1)[0];

  quill.insertEmbed(range.index, "break", true, Quill.sources.USER);

  if (nextLeaf === null || currentLeaf.parent !== nextLeaf.parent) {
    quill.insertEmbed(range.index, "break", true, Quill.sources.USER);
  }
  quill.setSelection(range.index + 1, Quill.sources.SILENT);

  Object.keys(context.format).forEach((name) => {
    if (Parchment.query(name, Parchment.Scope.BLOCK)) return;
    if (Array.isArray(context.format[name])) return;
    if (name === "link") return;
    quill.format(name, context.format[name], Quill.sources.USER);
  });
}

function handleEnter(range, context) {
  const { quill } = this;

  if (range.length > 0) {
    quill.scroll.deleteAt(range.index, range.length);
  }
  const lineFormats = Object.keys(context.format).reduce((acc, format) => {
    if (
      Parchment.query(format, Parchment.Scope.BLOCK) &&
      !Array.isArray(context.format[format])
    ) {
      acc[format] = context.format[format];
    }
    return acc;
  }, {});

  const previousChar = quill.getText(range.index - 1, 1);

  quill.insertText(range.index, "\n", lineFormats, Quill.sources.USER);

  if (
    previousChar == "" ||
    (previousChar == "\n" &&
      !(context.offset > 0 && context.prefix.length === 0))
  ) {
    quill.setSelection(range.index + 2, Quill.sources.SILENT);
  } else {
    quill.setSelection(range.index + 1, Quill.sources.SILENT);
  }
  Object.keys(context.format).forEach((name) => {
    if (lineFormats[name] != null) return;
    if (Array.isArray(context.format[name])) return;
    if (name === "link") return;
    quill.format(name, context.format[name], Quill.sources.USER);
  });
}

const Editor = ({ onChange, value, readOnly = false ,bullets=false}) => {
  const quillRef = useRef(null);
  const isInitialMount = useRef(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });

  const removeExtraneousLines = () => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const length = quill.getLength();
    const text = quill.getText(length - 2, 2);

    if (text === "\n\n") {
      quill.deleteText(quill.getLength() - 2, 2);
    }
  };

  useEffect(() => {
    removeExtraneousLines();
  }, []);

  useEffect(() => {
    if (quillRef.current && value !== undefined && value !== null && value !== '') {
      const quill = quillRef.current.getEditor();
      const currentHTML = quill.root.innerHTML.trim();
      const newValue = value.trim();
      if (currentHTML !== newValue) {
        quill.clipboard.dangerouslyPasteHTML(newValue, 'silent');
      }
    }
    isInitialMount.current = false;
  }, [value]);

  const handleEmojiClick = useCallback(() => {
    if (!quillRef.current) return;
    
    const quill = quillRef.current.getEditor();
    const selection = quill.getSelection();
    if (selection) {
      const bounds = quill.getBounds(selection.index);
      const editorContainer = quill.container;
      const containerWidth = editorContainer.offsetWidth;
      const containerHeight = editorContainer.offsetHeight;
      const pickerWidth = 300;
      const pickerHeight = 350;
      
      const spaceOnRight = containerWidth - bounds.left;
      const spaceOnLeft = bounds.left;
      
      const spaceBelow = containerHeight - (bounds.top + bounds.height);
      const spaceAbove = bounds.top;
      
      let leftPosition;
      let topPosition;


      if (spaceOnRight >= pickerWidth) {
        leftPosition = bounds.left;
      } else if (spaceOnLeft >= pickerWidth) {
        leftPosition = bounds.left - pickerWidth;
      } else {
        if (bounds.left < containerWidth / 2) {
          leftPosition = 0;
        } else {
          leftPosition = containerWidth - pickerWidth;
        }
      }

      if (spaceBelow >= pickerHeight) {
        topPosition = bounds.top + bounds.height + 5;
      } else if (spaceAbove >= pickerHeight) {
        topPosition = bounds.top - pickerHeight - 5;
      } else {
        if (bounds.top < containerHeight / 2) {
          topPosition = 5;
        } else {
          topPosition = containerHeight - pickerHeight - 5;
        }
      }

      setEmojiPickerPosition({
        top: topPosition,
        left: leftPosition
      });
    }
    setShowEmojiPicker(prev => !prev);
  }, []);

  const handleEmojiSelect = (emoji) => {
    if (!quillRef.current) return;
    
    const quill = quillRef.current.getEditor();
    let selection = quill.getSelection(true);
    if (!selection) {
      selection = { index: quill.getLength() - 1, length: 0 };
    }
    
    let emojiChar;
    
    if (emoji.code_decimal) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = emoji.code_decimal;
      emojiChar = tempDiv.textContent || tempDiv.innerText;
    } else if (emoji.unicode) {
      emojiChar = String.fromCodePoint(parseInt(emoji.unicode, 16));
    } else {
      emojiChar = emoji.name;
    }
    quill.insertText(selection.index, emojiChar, 'user');
    quill.setSelection(selection.index + emojiChar.length, 'user');  
  };

  useEffect(() => {
    if (quillRef.current) {
      const toolbarElement = quillRef.current.getEditor().container.previousSibling;
      
      if (toolbarElement) {
        const emojiButton = toolbarElement.querySelector('.ql-emoji');
        if (emojiButton) {
          emojiButton.innerHTML = '<svg viewBox="0 0 18 18" width="18" height="18"><circle class="ql-fill" cx="7" cy="7" r="1"></circle><circle class="ql-fill" cx="11" cy="7" r="1"></circle><path class="ql-stroke" d="M7,10a2,2,0,0,0,4,0H7Z"></path><circle class="ql-stroke" cx="9" cy="9" r="6"></circle></svg>';
          emojiButton.setAttribute('title', 'Emoji');
        }
      }
    }
  }, []);


  return (
    <div className="quill-editor-wrapper" style={{ position: 'relative' }}>
      <ReactQuill
        ref={quillRef}
        className="editor custom-quill"
        theme="snow"
        readOnly={readOnly}
        modules={{
          toolbar: {
            container: [
              [{ 'header': [2,3,false] }],
              ['bold', 'italic', 'underline', 'strike'],
              ['emoji'],
              [{ 'color': ["#4D4F59","white","black","green","red","blue","yellow","purple"] }, { 'background': ["#4D4F59","white","black","green","red","blue","yellow","purple"] }],
              [{ 'align': [] }],
              [ bullets?{ 'list': 'ordered'}:[""], bullets ? { 'list': 'bullet' } : [""]],
            ],
            handlers: {
              'emoji': handleEmojiClick
            }
          },
          clipboard: {
            matchers: [["BR", lineBreakMatcher]],
            matchVisual: false
          },
          keyboard: {
            bindings: { 
              break: {
                key: 13,
                handler: handleEnter
              },
              linebreak: {
                key: 13,
                shiftKey: true,
                handler: handleLinebreak
              }
            }
          },
        }}
        value={value || ''}
        onChange={onChange}
      />
      {showEmojiPicker && (
        <div style={{ 
          position: 'absolute', 
          top: emojiPickerPosition.top, 
          left: emojiPickerPosition.left,
          zIndex: 999999
        }}>
          <EmojiPicker 
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Editor;
